import { useState, useCallback } from 'react';
import { useWorkspace } from '../context/WorkspaceContext';
import { apiFetch } from '../fetchInterceptor';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '../components/ui/alert';
import { Progress } from '../components/ui/progress';
import { 
  Upload,
  FileText,
  Download,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Loader2,
  ArrowRight,
  Receipt
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function BatchImport() {
  const { currentWorkspace } = useWorkspace();
  const [importType, setImportType] = useState('invoices');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [committing, setCommitting] = useState(false);
  const [result, setResult] = useState(null);
  
  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
      setPreview(null);
      setResult(null);
    }
  };
  
  const handleDownloadTemplate = async (type) => {
    try {
      const response = await apiFetch(
        `${API_URL}/api/batch-import/template/${type}`
      );
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${type}_template.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    } catch (error) {
      toast.error('Failed to download template');
    }
  };
  
  const handleUpload = async () => {
    if (!file || !currentWorkspace) return;
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('import_type', importType);
      formData.append('workspace_id', currentWorkspace.workspace_id);
      
      const response = await apiFetch(`${API_URL}/api/batch-import/preview`, {
        method: 'POST',
        body: formData
      });
      
      if (response.ok) {
        const data = await response.json();
        setPreview(data);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to parse CSV');
      }
    } catch (error) {
      toast.error('Failed to upload file');
    } finally {
      setUploading(false);
    }
  };
  
  const handleCommit = async () => {
    if (!preview) return;
    
    setCommitting(true);
    try {
      const response = await apiFetch(
        `${API_URL}/api/batch-import/${preview.import_id}/commit`,
        { method: 'POST' }
      );
      
      if (response.ok) {
        const data = await response.json();
        setResult(data);
        setPreview(null);
        setFile(null);
        toast.success(`Created ${data.records_created} draft ${importType}`);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Import failed');
      }
    } catch (error) {
      toast.error('Import failed');
    } finally {
      setCommitting(false);
    }
  };
  
  const validPercent = preview ? Math.round((preview.valid_rows / preview.total_rows) * 100) : 0;
  
  return (
    <div className="space-y-6" data-testid="batch-import-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Batch Import</h1>
          <p className="text-muted-foreground">Import invoices or bills from CSV</p>
        </div>
      </div>
      
      {/* Import Type Selection */}
      <Tabs value={importType} onValueChange={(v) => { setImportType(v); setPreview(null); setFile(null); setResult(null); }}>
        <TabsList>
          <TabsTrigger value="invoices" className="flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Invoices (AR)
          </TabsTrigger>
          <TabsTrigger value="bills" className="flex items-center gap-2">
            <Receipt className="w-4 h-4" />
            Bills (AP)
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value={importType} className="mt-6 space-y-6">
          {/* Template Download */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Step 1: Download Template</CardTitle>
              <CardDescription>
                Download the CSV template and fill in your {importType === 'invoices' ? 'invoice' : 'bill'} data
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button variant="outline" onClick={() => handleDownloadTemplate(importType)} data-testid="download-template-btn">
                <Download className="w-4 h-4 mr-2" />
                Download {importType === 'invoices' ? 'Invoice' : 'Bill'} Template
              </Button>
              
              <div className="mt-4 p-3 bg-muted rounded-lg text-sm">
                <p className="font-medium mb-2">Required columns:</p>
                <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                  <li><code className="text-xs bg-background px-1 rounded">{importType === 'invoices' ? 'customer_name' : 'vendor_name'}</code> - Must match existing contact</li>
                  <li><code className="text-xs bg-background px-1 rounded">{importType === 'invoices' ? 'invoice_date' : 'bill_date'}</code> - YYYY-MM-DD or MM/DD/YYYY</li>
                  <li><code className="text-xs bg-background px-1 rounded">due_date</code> - YYYY-MM-DD or MM/DD/YYYY</li>
                  <li><code className="text-xs bg-background px-1 rounded">description</code> - Line item description</li>
                  <li><code className="text-xs bg-background px-1 rounded">quantity</code> - Number (default: 1)</li>
                  <li><code className="text-xs bg-background px-1 rounded">unit_price</code> - Amount per unit</li>
                  <li><code className="text-xs bg-background px-1 rounded">account_code</code> - Must match existing account</li>
                </ul>
              </div>
            </CardContent>
          </Card>
          
          {/* File Upload */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Step 2: Upload CSV</CardTitle>
              <CardDescription>
                Upload your filled CSV file for validation
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-4">
                <Input
                  type="file"
                  accept=".csv"
                  onChange={handleFileChange}
                  className="max-w-xs"
                  data-testid="csv-file-input"
                />
                <Button onClick={handleUpload} disabled={!file || uploading} data-testid="upload-csv-btn">
                  {uploading ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Validating...</>
                  ) : (
                    <><Upload className="w-4 h-4 mr-2" /> Validate & Preview</>
                  )}
                </Button>
              </div>
              
              {file && !preview && (
                <p className="text-sm text-muted-foreground">
                  Selected: {file.name} ({(file.size / 1024).toFixed(1)} KB)
                </p>
              )}
            </CardContent>
          </Card>
          
          {/* Preview */}
          {preview && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Step 3: Review & Import</CardTitle>
                <CardDescription>
                  Review the validation results before importing
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Summary */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="p-4 bg-muted rounded-lg text-center">
                    <p className="text-2xl font-bold">{preview.total_rows}</p>
                    <p className="text-sm text-muted-foreground">Total Rows</p>
                  </div>
                  <div className="p-4 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg text-center">
                    <p className="text-2xl font-bold text-emerald-600">{preview.valid_rows}</p>
                    <p className="text-sm text-muted-foreground">Valid</p>
                  </div>
                  <div className="p-4 bg-rose-100 dark:bg-rose-900/30 rounded-lg text-center">
                    <p className="text-2xl font-bold text-rose-600">{preview.invalid_rows}</p>
                    <p className="text-sm text-muted-foreground">Invalid</p>
                  </div>
                </div>
                
                <Progress value={validPercent} className="h-2" />
                
                {preview.invalid_rows > 0 && (
                  <Alert variant="destructive">
                    <AlertTriangle className="w-4 h-4" />
                    <AlertTitle>Validation Errors</AlertTitle>
                    <AlertDescription>
                      {preview.invalid_rows} row(s) have errors and will be skipped. Fix the issues in your CSV and re-upload, or proceed with valid rows only.
                    </AlertDescription>
                  </Alert>
                )}
                
                {/* Preview Table */}
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[60px]">Row</TableHead>
                        <TableHead className="w-[60px]">Status</TableHead>
                        <TableHead>{importType === 'invoices' ? 'Customer' : 'Vendor'}</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead>Account</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                        <TableHead>Errors</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {preview.preview.map((row) => (
                        <TableRow key={row.row} className={cn(!row.valid && 'bg-rose-50 dark:bg-rose-900/10')}>
                          <TableCell className="font-mono text-xs">{row.row}</TableCell>
                          <TableCell>
                            {row.valid ? (
                              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                            ) : (
                              <XCircle className="w-4 h-4 text-rose-600" />
                            )}
                          </TableCell>
                          <TableCell>{row.data.contact_name || row.data[importType === 'invoices' ? 'customer_name' : 'vendor_name'] || '-'}</TableCell>
                          <TableCell>{row.data[importType === 'invoices' ? 'invoice_date' : 'bill_date'] || '-'}</TableCell>
                          <TableCell className="max-w-[200px] truncate">{row.data.description || '-'}</TableCell>
                          <TableCell>{row.data.account_code || '-'}</TableCell>
                          <TableCell className="text-right font-mono">
                            {row.data.quantity && row.data.unit_price 
                              ? `$${(row.data.quantity * row.data.unit_price).toFixed(2)}`
                              : '-'}
                          </TableCell>
                          <TableCell>
                            {row.errors.length > 0 && (
                              <div className="text-xs text-rose-600 space-y-1">
                                {row.errors.map((err, i) => (
                                  <div key={i}>{err.field}: {err.error}</div>
                                ))}
                              </div>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                
                {preview.total_rows > 50 && (
                  <p className="text-sm text-muted-foreground text-center">
                    Showing first 50 rows of {preview.total_rows}
                  </p>
                )}
                
                {/* Import Button */}
                <div className="flex justify-end gap-2 pt-4 border-t">
                  <Button variant="outline" onClick={() => { setPreview(null); setFile(null); }}>
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleCommit} 
                    disabled={committing || preview.valid_rows === 0}
                    data-testid="commit-import-btn"
                  >
                    {committing ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Importing...</>
                    ) : (
                      <><ArrowRight className="w-4 h-4 mr-2" /> Import {preview.valid_rows} {importType === 'invoices' ? 'Invoice(s)' : 'Bill(s)'}</>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
          
          {/* Success Result */}
          {result && (
            <Alert className="bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200">
              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
              <AlertTitle className="text-emerald-800 dark:text-emerald-400">Import Successful!</AlertTitle>
              <AlertDescription>
                Created {result.records_created} draft {importType}. 
                <Link 
                  to={importType === 'invoices' ? '/invoices' : '/bills'} 
                  className="ml-2 font-medium underline"
                >
                  View {importType === 'invoices' ? 'Invoices' : 'Bills'}
                </Link>
              </AlertDescription>
            </Alert>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
